import { c as _c } from "react/compiler-runtime";
import type { Icon } from '@onefootprint/icons';
import { IcoChevronLeft16, IcoChevronRight16 } from '@onefootprint/icons';
import { cx } from 'class-variance-authority';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHorizontalScrollFade } from '../../hooks/use-horizontal-scroll-fade';
export type SuggestedAction = {
  id: string;
  label: string;
  icon: Icon;
  prompt: string;
};
type SuggestedActionsProps = {
  actions: SuggestedAction[];
  onSelect: (prompt: string) => void;
  disabled?: boolean;
  /** Negative margin + matching padding (e.g. `-mx-3 px-3`) so the row bleeds past the parent's padding. */
  scrollerClassName?: string;
};
const FADE_PX = 32;

// Wider than the attached-files mask: a white pill on a white surface gives the
// fade only its border and label to act on.
const buildFade = (fadeStart: boolean, fadeEnd: boolean) => {
  const head = fadeStart ? `transparent 0, rgba(0,0,0,0.25) ${FADE_PX / 2}px, #000 ${FADE_PX}px` : '#000 0';
  const tail = fadeEnd ? `#000 calc(100% - ${FADE_PX}px), rgba(0,0,0,0.25) calc(100% - ${FADE_PX / 2}px), transparent 100%` : '#000 100%';
  return `linear-gradient(to right, ${head}, ${tail})`;
};
const Arrow = t0 => {
  const $ = _c(10);
  const {
    ariaLabel,
    icon: ArrowIcon,
    onClick,
    disabled
  } = t0;
  const t1 = disabled ? "cursor-default opacity-30" : "cursor-pointer hover:bg-hover";
  let t2;
  if ($[0] !== t1) {
    t2 = cx("flex size-5 items-center justify-center rounded transition-colors", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== ArrowIcon) {
    t3 = <ArrowIcon color="tertiary" />;
    $[2] = ArrowIcon;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== ariaLabel || $[5] !== disabled || $[6] !== onClick || $[7] !== t2 || $[8] !== t3) {
    t4 = <button aria-label={ariaLabel} className={t2} disabled={disabled} onClick={onClick} type="button">{t3}</button>;
    $[4] = ariaLabel;
    $[5] = disabled;
    $[6] = onClick;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
const SuggestedActions = t0 => {
  const $ = _c(38);
  const {
    actions,
    onSelect,
    disabled,
    scrollerClassName
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "suggestedActions"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("ask-percy", t1);
  const {
    ref,
    canScrollBack,
    canScrollForward,
    scrollByPage
  } = useHorizontalScrollFade(actions.length);
  const [isHidden, setIsHidden] = useState(false);
  let t2;
  if ($[1] !== canScrollBack || $[2] !== canScrollForward) {
    t2 = buildFade(canScrollBack, canScrollForward);
    $[1] = canScrollBack;
    $[2] = canScrollForward;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const fade = t2;
  if (actions.length === 0) {
    return null;
  }
  let t3;
  if ($[4] !== t) {
    t3 = t(_temp);
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <p className="text-caption-3 font-medium text-tertiary">{t3}</p>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span aria-hidden={true} className="text-caption-3 text-quaternary">·</span>;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => setIsHidden(_temp2);
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = isHidden ? _temp3 : _temp4;
  let t8;
  if ($[10] !== t || $[11] !== t7) {
    t8 = t(t7);
    $[10] = t;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t8) {
    t9 = <button className="cursor-pointer text-caption-3 font-medium text-tertiary underline-offset-2 transition-colors hover:text-primary hover:underline" onClick={t6} type="button">{t8}</button>;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t4 || $[16] !== t9) {
    t10 = <div className="flex min-w-0 items-center gap-1">{t4}{t5}{t9}</div>;
    $[15] = t4;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== canScrollBack || $[19] !== canScrollForward || $[20] !== isHidden || $[21] !== scrollByPage || $[22] !== t) {
    t11 = !isHidden && <div className="flex shrink-0 items-center gap-0.5 max-md:hidden"><Arrow ariaLabel={t(_temp5)} disabled={!canScrollBack} icon={IcoChevronLeft16} onClick={() => scrollByPage("back")} /><Arrow ariaLabel={t(_temp6)} disabled={!canScrollForward} icon={IcoChevronRight16} onClick={() => scrollByPage("forward")} /></div>;
    $[18] = canScrollBack;
    $[19] = canScrollForward;
    $[20] = isHidden;
    $[21] = scrollByPage;
    $[22] = t;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t10 || $[25] !== t11) {
    t12 = <div className="flex items-center justify-between gap-2">{t10}{t11}</div>;
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== actions || $[28] !== disabled || $[29] !== fade || $[30] !== isHidden || $[31] !== onSelect || $[32] !== ref || $[33] !== scrollerClassName) {
    t13 = !isHidden && <div ref={ref} className={cx("flex gap-2 overflow-x-auto [&::-webkit-scrollbar]:hidden", scrollerClassName)} style={{
      scrollbarWidth: "none",
      maskImage: fade,
      WebkitMaskImage: fade
    }}>{actions.map(action => {
        const ActionIcon = action.icon;
        return <button key={action.id} type="button" disabled={disabled} onClick={() => onSelect(action.prompt)} className={cx("flex h-7 shrink-0 items-center gap-1.5 rounded-full border border-tertiary px-2.5 text-label-3 transition-colors", disabled ? "cursor-default text-quaternary" : "cursor-pointer text-tertiary hover:bg-hover hover:text-primary")}><ActionIcon className="shrink-0" /><span className="whitespace-nowrap">{action.label}</span></button>;
      })}</div>;
    $[27] = actions;
    $[28] = disabled;
    $[29] = fade;
    $[30] = isHidden;
    $[31] = onSelect;
    $[32] = ref;
    $[33] = scrollerClassName;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== t12 || $[36] !== t13) {
    t14 = <div className="flex w-full flex-col gap-1.5 group-data-[keyboard-open]/app:hidden">{t12}{t13}</div>;
    $[35] = t12;
    $[36] = t13;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  return t14;
};
export default SuggestedActions;
function _temp($) {
  return $.title;
}
function _temp2(current) {
  return !current;
}
function _temp3($_0) {
  return $_0.show;
}
function _temp4($_1) {
  return $_1.hide;
}
function _temp5($_2) {
  return $_2.back;
}
function _temp6($_3) {
  return $_3.forward;
}